import React, { useState, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Environment, Html } from '@react-three/drei';

// Componente para criar uma unidade 3D simples
const UnitModel = ({ position, color, unitData, onClick, isHighlighted }) => {
  const meshRef = useRef();
  
  // Efeito de hover para unidades destacadas
  const [hovered, setHovered] = useState(false);
  
  // Dimensões da unidade
  const width = 1;
  const height = 0.8;
  const depth = 1.2;
  
  return (
    <group 
      position={position}
      onClick={(e) => {
        e.stopPropagation();
        onClick(unitData);
      }}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
    >
      {/* Modelo da unidade */}
      <mesh 
        ref={meshRef}
        castShadow
      >
        <boxGeometry args={[width, height, depth]} />
        <meshStandardMaterial 
          color={color} 
          opacity={0.8}
          transparent={true}
          emissive={isHighlighted || hovered ? color : '#000000'}
          emissiveIntensity={isHighlighted || hovered ? 0.3 : 0}
        />
      </mesh>
      
      {/* Número da unidade */}
      <Html position={[0, 0, depth/2 + 0.01]} center>
        <div className={`
          px-1 py-0.5 text-xs rounded-md text-white font-bold
          ${isHighlighted ? 'bg-blue-600 ring-2 ring-white' : 'bg-gray-700'}
        `}>
          {unitData.number}
        </div>
      </Html>
    </group>
  );
};

// Componente que cria o andar do edifício
const FloorModel = ({ floorNumber, units = [], onUnitClick, highlightedUnitId }) => {
  // Posicionar o andar baseado no número do andar
  const yPosition = floorNumber * 1.2;
  
  // Determinar a cor baseada no status da unidade
  const getUnitColor = (status) => {
    switch(status) {
      case 'available': return '#22c55e'; // verde
      case 'reserved': return '#eab308';  // amarelo
      case 'sold': return '#ef4444';      // vermelho
      default: return '#94a3b8';         // cinza
    }
  };
  
  return (
    <group position={[0, yPosition, 0]}>
      {/* Base do andar */}
      <mesh position={[0, -0.4, 0]} receiveShadow>
        <boxGeometry args={[10, 0.2, 8]} />
        <meshStandardMaterial color="#e2e8f0" />
      </mesh>
      
      {/* Unidades do andar */}
      {units.map((unit, index) => {
        // Calcular posição em grid
        const row = Math.floor(index / 3);
        const col = index % 3;
        const xPos = (col - 1) * 2.5;
        const zPos = (row - 0.5) * 2.5;
        
        return (
          <UnitModel
            key={unit.id}
            position={[xPos, 0, zPos]}
            color={getUnitColor(unit.status)}
            unitData={unit}
            onClick={onUnitClick}
            isHighlighted={unit.id === highlightedUnitId}
          />
        );
      })}
    </group>
  );
};

// Componente que cria o edifício completo
const BuildingModel = ({ floors = [4, 5, 6, 7, 8, 9, 10], selectedFloor = 10, unitsByFloor = () => [], onUnitClick, highlightedUnitId }) => {
  return (
    <group position={[0, 0, 0]}>
      {/* Estrutura base do edifício */}
      <mesh position={[0, -1, 0]} receiveShadow>
        <boxGeometry args={[12, 1, 10]} />
        <meshStandardMaterial color="#cbd5e1" />
      </mesh>
      
      {/* Renderizar cada andar */}
      {floors.map(floor => (
        <FloorModel
          key={floor}
          floorNumber={floor - floors[0]} // Normalizar para começar do zero
          units={unitsByFloor(floor) || []}
          onUnitClick={onUnitClick}
          highlightedUnitId={highlightedUnitId}
        />
      ))}
    </group>
  );
};

const VirtualModelViewer = ({ 
  onUnitSelect, 
  activeFloor = 10,
  viewMode = 'day',
  showFurniture = true,
  floors = [4, 5, 6, 7, 8, 9, 10],
  unitsByFloor = () => []
}) => {
  const [highlightedUnitId, setHighlightedUnitId] = useState(null);

  const handleUnitClick = (unit) => {
    setHighlightedUnitId(unit.id);
    onUnitSelect && onUnitSelect(unit.id);
  };

  return (
    <div className="virtual-model-container h-full w-full">
      <Canvas shadows camera={{ position: [10, 10, 10], fov: 50 }}>
        {/* Luzes */}
        <ambientLight intensity={0.5} />
        <directionalLight 
          position={[5, 8, 5]} 
          intensity={1} 
          castShadow 
          shadow-mapSize-width={2048} 
          shadow-mapSize-height={2048}
        />
        <Environment preset={viewMode === 'day' ? "sunset" : "night"} />
        
        {/* Modelo do edifício */}
        <BuildingModel 
          floors={floors}
          selectedFloor={activeFloor}
          unitsByFloor={unitsByFloor}
          onUnitClick={handleUnitClick}
          highlightedUnitId={highlightedUnitId}
        />

        {/* Controles de câmera */}
        <OrbitControls 
          enablePan={true}
          enableZoom={true}
          enableRotate={true}
          minDistance={5}
          maxDistance={20}
          target={[0, (activeFloor - floors[0]) * 1.2, 0]} // Foco no andar selecionado
        />
      </Canvas>
    </div>
  );
};

export default VirtualModelViewer;